import {
  bodySlotOf,
  BODY_SHAPES,
  CLASSES,
  PLAYABLE_CLASS_IDS,
  type ClassId,
  DEFAULT_LOOK,
  EYE_COLORS,
  HAIR_COLORS,
  HAIR_STYLES,
  SKIN_COLORS,
  type Direction,
  type Equipped,
  type Look,
  type LookColor,
  lookTints,
  startingKit,
} from '@shefira/shared';
import { loadCharacterAtlas } from '../assets/dom-atlas.js';
import { DollPreview } from './doll-preview.js';
import { HEAD_FRAME } from './portrait.js';
import { uiHtml, uiText } from './text.js';
import './customise.css';

export interface Customisation {
  classId: ClassId;
  look: Look;
}

const DIRECTIONS = [
  { id: 'south', name: 'Face' },
  { id: 'east', name: 'Profil droit' },
  { id: 'north', name: 'Dos' },
  { id: 'west', name: 'Profil gauche' },
] as const satisfies readonly { id: Direction; name: string }[];

function swatches(colors: readonly LookColor[], group: 'hair' | 'eyes' | 'skin'): string {
  return colors
    .map(
      (
        color,
      ) => `<button class="customise__swatch" type="button" data-group="${group}" data-value="${color.id}"
    style="--swatch: ${color.swatch ?? color.hex}" title="${color.name}" aria-label="${color.name}" aria-pressed="false">
    <span class="customise__swatch-ink" aria-hidden="true"></span></button>`,
    )
    .join('');
}

function startingOutfit(classId: ClassId): Equipped {
  const kit: Equipped = {};
  for (const template of startingKit(classId)) {
    const slot = bodySlotOf(template);
    if (slot === undefined) continue;
    kit[slot] = {
      id: template.id,
      templateId: template.id,
      quantity: 1,
      upgradeLevel: 0,
      bound: false,
    };
  }
  return kit;
}

export function showCustomise(title = 'Ton personnage'): Promise<Customisation | null> {
  return new Promise((resolve) => {
    void loadCharacterAtlas().then((atlas) => {
      const playable: readonly ClassId[] = PLAYABLE_CLASS_IDS;
      const state: Customisation = { classId: playable[0] ?? 'warrior', look: { ...DEFAULT_LOOK } };
      let direction: Direction = 'south';
      const overlay = document.createElement('div');
      overlay.className = 'login';
      overlay.innerHTML = uiHtml(`
        <main class="login__panel customise" aria-label="Personnaliser ton personnage">
          <header class="customise__header"><h1 class="login__title">${title}</h1><p>Choisis son apparence avant d'entrer dans Shefira.</p></header>
          <div class="customise__layout">
            <section class="customise__stage" aria-label="Aperçu du personnage">
              <div class="customise__preview"></div>
              <div class="customise__directions" role="group" aria-label="Faire tourner le personnage">
                <button type="button" data-rotate="-1" aria-label="Tourner vers la gauche"><span aria-hidden="true"></span></button>
                <span class="customise__direction-name" aria-live="polite">Face</span>
                <button type="button" data-rotate="1" aria-label="Tourner vers la droite"><span aria-hidden="true"></span></button>
              </div>
              <p class="customise__preview-note">Avec l'équipement de départ</p>
            </section>
            <div class="customise__choices">
              ${playable.length > 1 ? `<section class="customise__field"><h2>Classe</h2><div class="customise__styles">${playable.map((classId) => `<button type="button" data-class="${classId}" aria-pressed="false">${CLASSES[classId].name}</button>`).join('')}</div></section>` : `<p class="customise__class-name">${CLASSES[state.classId].name}</p>`}
              <section class="customise__field"><h2>Corps</h2><div class="customise__styles" role="group" aria-label="Corps">
                ${BODY_SHAPES.map((shape) => `<button type="button" data-body="${shape.id}" aria-pressed="false">${shape.name}</button>`).join('')}
              </div></section>
              <section class="customise__field"><h2>Coiffure</h2><div class="customise__styles customise__styles--portraits" role="group" aria-label="Coiffure">
                ${HAIR_STYLES.map((style) => `<button type="button" data-style="${style.id}" aria-label="${style.name}" title="${style.name}" aria-pressed="false"></button>`).join('')}
              </div></section>
              <section class="customise__field"><div class="customise__field-head"><h2>Couleur de peau</h2><span data-selected="skin"></span></div>
                <div class="customise__swatches" role="group" aria-label="Couleur de peau">${swatches(SKIN_COLORS, 'skin')}</div></section>
              <section class="customise__field"><div class="customise__field-head"><h2>Couleur des cheveux</h2><span data-selected="hair"></span></div>
                <div class="customise__swatches" role="group" aria-label="Couleur des cheveux">${swatches(HAIR_COLORS, 'hair')}</div></section>
              <section class="customise__field"><div class="customise__field-head"><h2>Couleur des yeux</h2><span data-selected="eyes"></span></div>
                <div class="customise__swatches" role="group" aria-label="Couleur des yeux">${swatches(EYE_COLORS, 'eyes')}</div></section>
              <button class="customise__shuffle" type="button">Surprends-moi</button>
            </div>
          </div>
          <div class="customise__actions">
            <button class="customise__back" type="button">Retour</button>
            <button class="login__submit login__submit--gold customise__submit" type="button">Créer ce personnage</button>
          </div>
        </main>`);
      document.body.append(overlay);
      const previewHost = overlay.querySelector<HTMLElement>('.customise__preview');

      const render = (): void => {
        for (const card of overlay.querySelectorAll<HTMLButtonElement>(
          '[data-class], [data-body], [data-style], [data-group]',
        )) {
          const active =
            card.dataset.class === state.classId ||
            card.dataset.body === state.look.body ||
            card.dataset.style === state.look.hairStyle ||
            (card.dataset.group === 'hair' && card.dataset.value === state.look.hair) ||
            (card.dataset.group === 'eyes' && card.dataset.value === state.look.eyes) ||
            (card.dataset.group === 'skin' && card.dataset.value === state.look.skin);
          card.classList.toggle('is-selected', active);
          card.setAttribute('aria-pressed', String(active));
          if (card.dataset.group === 'hair') card.disabled = state.look.hairStyle === 'shaved';
        }
        const hairLabel = overlay.querySelector<HTMLElement>('[data-selected="hair"]');
        const eyeLabel = overlay.querySelector<HTMLElement>('[data-selected="eyes"]');
        const skinLabel = overlay.querySelector<HTMLElement>('[data-selected="skin"]');
        if (hairLabel !== null)
          hairLabel.textContent =
            state.look.hairStyle === 'shaved'
              ? uiText('Masquée')
              : (HAIR_COLORS.find((color) => color.id === state.look.hair)?.name ?? '');
        if (eyeLabel !== null)
          eyeLabel.textContent =
            EYE_COLORS.find((color) => color.id === state.look.eyes)?.name ?? '';
        if (skinLabel !== null)
          skinLabel.textContent =
            SKIN_COLORS.find((color) => color.id === state.look.skin)?.name ?? '';
        const directionLabel = overlay.querySelector<HTMLElement>('.customise__direction-name');
        if (directionLabel !== null)
          directionLabel.textContent = uiText(
            DIRECTIONS.find((view) => view.id === direction)?.name ?? '',
          );
        if (atlas === null || previewHost === null) return;
        const prefix = atlas.prefixOf(`body.${state.classId}`);
        if (prefix === null) return;
        const preview = new DollPreview({
          bodyKey: prefix,
          frame: atlas.frame,
          featureTints: lookTints(state.look),
          hairStyle: state.look.hairStyle,
          body: state.look.body,
          scale: 6,
        });
        preview.draw(startingOutfit(state.classId), direction);
        previewHost.replaceChildren(preview.canvas);
        for (const styleButton of overlay.querySelectorAll<HTMLButtonElement>('[data-style]')) {
          const style = HAIR_STYLES.find((item) => item.id === styleButton.dataset.style);
          if (style === undefined) continue;
          const head = new DollPreview({
            bodyKey: prefix,
            frame: atlas.frame,
            featureTints: lookTints(state.look),
            hairStyle: style.id,
            body: state.look.body,
            scale: 1,
          });
          head.draw({}, 'south');
          const crop = document.createElement('canvas');
          crop.width = HEAD_FRAME.size;
          crop.height = HEAD_FRAME.size;
          const context = crop.getContext('2d');
          if (context !== null) {
            context.imageSmoothingEnabled = false;
            context.drawImage(
              head.canvas,
              HEAD_FRAME.x,
              HEAD_FRAME.y,
              HEAD_FRAME.size,
              HEAD_FRAME.size,
              0,
              0,
              HEAD_FRAME.size,
              HEAD_FRAME.size,
            );
            styleButton.replaceChildren(crop);
          }
        }
      };

      overlay.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        const button = event.target.closest<HTMLButtonElement>('button');
        if (button === null) return;
        const selectedClass = playable.find((id) => id === button.dataset.class);
        if (selectedClass !== undefined) state.classId = selectedClass;
        else if (button.dataset.body !== undefined) {
          const shape = BODY_SHAPES.find((item) => item.id === button.dataset.body);
          if (shape !== undefined) state.look = { ...state.look, body: shape.id };
        } else if (button.dataset.style !== undefined) {
          const style = HAIR_STYLES.find((item) => item.id === button.dataset.style);
          if (style !== undefined) state.look = { ...state.look, hairStyle: style.id };
        }
        else if (button.dataset.rotate !== undefined) {
          const step = button.dataset.rotate === '-1' ? -1 : 1;
          const index = DIRECTIONS.findIndex((view) => view.id === direction);
          direction =
            DIRECTIONS[(index + step + DIRECTIONS.length) % DIRECTIONS.length]?.id ?? direction;
        } else if (button.dataset.group === 'skin') {
          const color = SKIN_COLORS.find((item) => item.id === button.dataset.value);
          if (color !== undefined) state.look = { ...state.look, skin: color.id };
        } else if (button.dataset.group === 'hair') {
          const color = HAIR_COLORS.find((item) => item.id === button.dataset.value);
          if (color !== undefined) state.look = { ...state.look, hair: color.id };
        } else if (button.dataset.group === 'eyes') {
          const color = EYE_COLORS.find((item) => item.id === button.dataset.value);
          if (color !== undefined) state.look = { ...state.look, eyes: color.id };
        } else if (button.classList.contains('customise__shuffle')) {
          const pick = <T>(items: readonly T[]): T | undefined =>
            items[Math.floor(Math.random() * items.length)];
          // The body stays as chosen: a surprise is about the looks.
          state.look = {
            body: state.look.body,
            hairStyle: pick(HAIR_STYLES)?.id ?? state.look.hairStyle,
            hair: pick(HAIR_COLORS)?.id ?? state.look.hair,
            eyes: pick(EYE_COLORS)?.id ?? state.look.eyes,
            skin: pick(SKIN_COLORS)?.id ?? state.look.skin,
          };
        } else if (button.classList.contains('customise__submit')) {
          overlay.remove();
          resolve(state);
          return;
        } else if (button.classList.contains('customise__back')) {
          overlay.remove();
          resolve(null);
          return;
        }
        render();
      });
      render();
    });
  });
}
